import { useState } from "react";
import LoginForm from "../components/LoginForm";
import { LANDING_PAGE_CONTENT } from "../constants";
import "../css/LandingPage.css";

const content = LANDING_PAGE_CONTENT;

// This React component renders the reusable brand block used in the header and footer.
function BrandLockup() {
  return (
    <a className="brand-lockup" href="#home" aria-label={`${content.brand.name} home`}>
      <span className="brand-mark" aria-hidden="true">
        {content.brand.initials}
      </span>
      <span>
        <strong>{content.brand.name}</strong>
        <small>{content.brand.tagline}</small>
      </span>
    </a>
  );
}

// This React component receives props, which are inputs passed from the component that uses it.
function SectionHeader({ eyebrow, heading, subheading }) {
  return (
    <div className="section-header">
      {/* Conditional rendering: only show the eyebrow/subheading if those props were provided. */}
      {eyebrow && <p className="section-eyebrow">{eyebrow}</p>}
      <h2>{heading}</h2>
      {subheading && <p>{subheading}</p>}
    </div>
  );
}

// This React component renders the visual mail/status card shown next to the hero copy.
function HeroVisual() {
  const visual = content.hero.visual;

  return (
    <aside className="hero-visual" aria-label={visual.title}>
      <div className="portal-card portal-card-large">
        <div className="portal-card-header">
          <div>
            <p>{visual.title}</p>
            <h2>{visual.status}</h2>
          </div>
          <span>{visual.badge}</span>
        </div>

        <div className="portal-list">
          {/* map() turns each item in the data array into one rendered JSX row. */}
          {visual.items.map((item) => (
            <div className="portal-list-item" key={`${item.sender}-${item.type}`}>
              <span className="item-dot" aria-hidden="true" />
              <div>
                <strong>{item.sender}</strong>
                <small>{item.type}</small>
              </div>
              <em>{item.status}</em>
            </div>
          ))}
        </div>

        <div className="portal-actions-preview" aria-label={visual.actionsTitle}>
          {/* key helps React track each repeated element when rendering lists. */}
          {visual.actions.map((action) => (
            <span key={action}>{action}</span>
          ))}
        </div>
      </div>
    </aside>
  );
}

// This React component renders a static preview of what the tenant portal table could look like.
function PortalPreview() {
  return (
    <div className="portal-preview" aria-label={content.portal.heading}>
      <div className="portal-browser-bar" aria-hidden="true">
        <span />
        <span />
        <span />
      </div>
      <div className="portal-table-wrap">
        <table>
          <thead>
            <tr>
              {/* Render each configured table header from constants.js. */}
              {content.portal.tableHeaders.map((header) => (
                <th key={header}>{header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* Render each example portal row from constants.js. */}
            {content.portal.rows.map((row) => (
              <tr key={`${row.date}-${row.sender}-${row.action}`}>
                <td>{row.date}</td>
                <td>{row.type}</td>
                <td>{row.sender}</td>
                <td>
                  <span className="status-pill">{row.status}</span>
                </td>
                <td>{row.action}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// This React component renders the top navigation and receives click handlers as props.
function LandingHeader({ onOpenLogin, onOpenRegister }) {
  return (
    <header className="landing-header">
      <BrandLockup />
      <nav className="landing-nav" aria-label="Primary navigation">
        {/* Each nav link in constants.js becomes one anchor tag. */}
        {content.nav.links.map((link) => (
          <a href={link.href} key={link.href}>
            {link.label}
          </a>
        ))}
      </nav>
      <div className="landing-actions">
        {/* onClick tells React which function to run when the button is clicked. */}
        <button className="text-action" type="button" onClick={onOpenLogin}>
          {content.nav.loginLabel}
        </button>
        <button className="primary-action" type="button" onClick={onOpenRegister}>
          {content.nav.registerLabel}
        </button>
      </div>
    </header>
  );
}

// This React component renders the main hero section and uses a prop to open registration.
function HeroSection({ onOpenRegister }) {
  return (
    <section className="landing-hero">
      <div className="landing-hero-copy">
        <p className="section-eyebrow">{content.hero.eyebrow}</p>
        <h1>{content.hero.headline}</h1>
        <p>{content.hero.subheadline}</p>
        <div className="hero-actions">
          <button className="primary-action" type="button" onClick={onOpenRegister}>
            {content.hero.primaryCta}
          </button>
          <a className="secondary-action" href="#how-it-works">
            {content.hero.secondaryCta}
          </a>
        </div>
        <ul className="hero-bullets">
          {/* Render one list item for each hero bullet stored in constants.js. */}
          {content.hero.bullets.map((bullet) => (
            <li key={bullet}>{bullet}</li>
          ))}
        </ul>
      </div>
      <HeroVisual />
    </section>
  );
}

// This React component renders the short trust/clarification band under the hero section.
function ClarificationSection() {
  return (
    <section className="clarification-strip" aria-label={content.clarification.headline}>
      <div className="clarification-intro">
        <h2>{content.clarification.headline}</h2>
        <p>{content.clarification.copy}</p>
      </div>
      <div className="trust-card-grid">
        {/* Render each clarification card from the content object. */}
        {content.clarification.cards.map((card) => (
          <article className="trust-card" key={card.title}>
            <h3>{card.title}</h3>
            <p>{card.copy}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

// This React component renders the services section of the landing page.
function ServicesSection() {
  return (
    <section className="landing-section" id={content.services.id}>
      <SectionHeader
        eyebrow={content.services.eyebrow}
        heading={content.services.heading}
        subheading={content.services.subheading}
      />
      <div className="card-grid three-column">
        {/* Render one service card for each service defined in constants.js. */}
        {content.services.cards.map((card) => (
          <article className="info-card" key={card.title}>
            <span className="card-marker" aria-hidden="true" />
            <h3>{card.title}</h3>
            <p>{card.copy}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

// This React component renders the step-by-step process section.
function ProcessSection() {
  return (
    <section className="landing-section process-section" id={content.howItWorks.id}>
      <SectionHeader eyebrow={content.howItWorks.eyebrow} heading={content.howItWorks.heading} />
      <div className="steps-grid">
        {/* index is the current item's position in the array, used here for the step number. */}
        {content.howItWorks.steps.map((step, index) => (
          <article className="step-card" key={step.title}>
            <span>{String(index + 1).padStart(2, "0")}</span>
            <h3>{step.title}</h3>
            <p>{step.copy}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

// This React component explains the tenant portal and includes the portal preview component.
function PortalSection() {
  return (
    <section className="landing-section portal-section">
      <div className="split-section">
        <div>
          <SectionHeader eyebrow={content.portal.eyebrow} heading={content.portal.heading} />
          <p className="section-copy">{content.portal.copy}</p>
          <ul className="feature-list">
            {/* Render one bullet for each portal feature. */}
            {content.portal.features.map((feature) => (
              <li key={feature}>{feature}</li>
            ))}
          </ul>
        </div>
        <PortalPreview />
      </div>
    </section>
  );
}

// This React component renders common use cases and a disclaimer.
function UseCasesSection() {
  return (
    <section className="landing-section use-case-section">
      <SectionHeader eyebrow={content.useCases.eyebrow} heading={content.useCases.heading} />
      <p className="wide-intro">{content.useCases.intro}</p>
      <div className="card-grid three-column">
        {/* Render one card for each possible use case. */}
        {content.useCases.cards.map((card) => (
          <article className="info-card use-card" key={card.title}>
            <h3>{card.title}</h3>
            <p>{card.copy}</p>
          </article>
        ))}
      </div>
      <p className="disclaimer">{content.useCases.disclaimer}</p>
    </section>
  );
}

// This React component renders the meeting-space section and uses a prop for its CTA button.
function ConferenceSection({ onOpenRegister }) {
  return (
    <section className="landing-section conference-section">
      <div className="split-section">
        <div>
          <SectionHeader eyebrow={content.conferenceRoom.eyebrow} heading={content.conferenceRoom.heading} />
          <p className="section-copy">{content.conferenceRoom.copy}</p>
          <button className="secondary-action" type="button" onClick={onOpenRegister}>
            {content.conferenceRoom.cta}
          </button>
        </div>
        <ul className="conference-list">
          {/* Render each conference-room bullet from constants.js. */}
          {content.conferenceRoom.bullets.map((bullet) => (
            <li key={bullet}>{bullet}</li>
          ))}
        </ul>
      </div>
    </section>
  );
}

// This React component renders the about section with multiple paragraphs.
function AboutSection() {
  return (
    <section className="landing-section about-section" id={content.about.id}>
      <SectionHeader eyebrow={content.about.eyebrow} heading={content.about.heading} />
      <div className="about-copy">
        {/* Render every about paragraph from the content array. */}
        {content.about.paragraphs.map((paragraph) => (
          <p key={paragraph}>{paragraph}</p>
        ))}
        <strong>{content.about.locationStatement}</strong>
      </div>
    </section>
  );
}

// This React component renders the FAQ section and owns the state for open/closed answers.
function FAQSection() {
  // useState lets React remember which FAQ items are currently open.
  const [openItems, setOpenItems] = useState(() => new Set([0]));

  // This event handler opens a closed FAQ item or closes an open FAQ item.
  const toggleItem = (index) => {
    // Calling setOpenItems tells React the state changed and the component should re-render.
    setOpenItems((current) => {
      // Create a new Set instead of changing the current one directly.
      const next = new Set(current);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  return (
    <section className="landing-section faq-section" id={content.faq.id}>
      <SectionHeader eyebrow={content.faq.eyebrow} heading={content.faq.heading} />
      <div className="faq-list">
        {/* Render one FAQ article for each question/answer pair. */}
        {content.faq.items.map((item, index) => {
          const isOpen = openItems.has(index);
          const panelId = `faq-panel-${index}`;
          const buttonId = `faq-button-${index}`;

          return (
            <article className="faq-item" key={item.question}>
              {/* This click event toggles the matching FAQ answer. */}
              <button
                aria-controls={panelId}
                aria-expanded={isOpen}
                className="faq-question"
                id={buttonId}
                onClick={() => toggleItem(index)}
                type="button"
              >
                <span>{item.question}</span>
                {/* Conditional rendering chooses the symbol based on whether this item is open. */}
                <span aria-hidden="true">{isOpen ? "-" : "+"}</span>
              </button>
              <div
                aria-labelledby={buttonId}
                className="faq-answer"
                hidden={!isOpen}
                id={panelId}
                role="region"
              >
                <p>{item.answer}</p>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}

// This React component renders the final call-to-action section near the bottom of the page.
function FinalCtaSection({ onOpenRegister }) {
  return (
    <section className="final-cta">
      <h2>{content.finalCta.heading}</h2>
      <p>{content.finalCta.copy}</p>
      <div className="hero-actions">
        <button className="primary-action light-action" type="button" onClick={onOpenRegister}>
          {content.finalCta.primaryCta}
        </button>
        <button className="secondary-action dark-action" type="button" onClick={onOpenRegister}>
          {content.finalCta.secondaryCta}
        </button>
      </div>
    </section>
  );
}

// This React component renders the footer and reuses the brand block.
function LandingFooter() {
  return (
    <footer className="landing-footer">
      <div className="footer-brand">
        <BrandLockup />
        <p>{content.brand.location}</p>
      </div>
      {/* Render each footer column and its list of links from constants.js. */}
      {content.footer.columns.map((column) => (
        <div className="footer-column" key={column.heading}>
          <h2>{column.heading}</h2>
          <ul>
            {column.links.map((link) => (
              <li key={link}>{link}</li>
            ))}
          </ul>
        </div>
      ))}
      <p className="footer-disclaimer">{content.footer.disclaimer}</p>
    </footer>
  );
}

// This React component renders either the login modal or the register message modal.
function AuthModal({ activeModal, onClose }) {
  // Returning null tells React to render nothing when no modal is active.
  if (!activeModal) {
    return null;
  }

  return (
    // onMouseDown on the backdrop lets users close the modal by clicking outside it.
    <div className="modal-backdrop" role="presentation" onMouseDown={onClose}>
      {/* Stop the modal itself from triggering the backdrop close handler. */}
      <section
        aria-labelledby={`${activeModal}-modal-title`}
        aria-modal="true"
        className="modal-window"
        onMouseDown={(event) => event.stopPropagation()}
        role="dialog"
      >
        <button className="modal-close" type="button" aria-label={content.modal.closeLabel} onClick={onClose}>
          x
        </button>

        {/* Conditional rendering chooses which modal content to show based on activeModal. */}
        {activeModal === "login" ? (
          <>
            <h2 id="login-modal-title">{content.modal.loginTitle}</h2>
            <p className="modal-intro">{content.modal.loginIntro}</p>
            <LoginForm />
          </>
        ) : (
          <>
            <h2 id="register-modal-title">{content.modal.registerTitle}</h2>
            <p className="modal-intro">{content.modal.registerIntro}</p>
            <button className="primary-action" type="button" onClick={onClose}>
              {content.modal.registerConfirm}
            </button>
          </>
        )}
      </section>
    </div>
  );
}

// This is the main React page component rendered by the "/" route in App.js.
export default function LandingPage() {
  // activeModal stores which modal is open: null, "login", or "register".
  const [activeModal, setActiveModal] = useState(null);

  // These helper functions update state; React re-renders the page after each state change.
  const closeModal = () => setActiveModal(null);
  const openLogin = () => setActiveModal("login");
  const openRegister = () => setActiveModal("register");

  return (
    <main className="landing-page" id="home">
      {/* Props pass the modal-opening functions down into child components that need them. */}
      <LandingHeader onOpenLogin={openLogin} onOpenRegister={openRegister} />
      <HeroSection onOpenRegister={openRegister} />
      <ClarificationSection />
      <ServicesSection />
      <ProcessSection />
      <PortalSection />
      <UseCasesSection />
      <ConferenceSection onOpenRegister={openRegister} />
      <AboutSection />
      <FAQSection />
      <FinalCtaSection onOpenRegister={openRegister} />
      <LandingFooter />
      <AuthModal activeModal={activeModal} onClose={closeModal} />
    </main>
  );
}
